/*
 * File: /src/services/configService.js
 * Created Date: Saturday June 15th 2019
 * Author: Nicolas Wegener, Roy Collins
 * -----
 * Last Modified: Sunday, 16th June 2019 10:38:36 am
 * Modified By: Nicolas Wegener (wegenern@gmail.com)
 * -----
 * Copyright (c) 2019 AdBase, Inc.
 */

import store from 'src/store/index';

import { authedAxios } from '../shared/axiosWrapper';
import { AD_BASE_APPCONFIG_PATH } from '../constants';

/*
 * Controls the loading and unload of all the config items as needed.
 * Use this at the entry point for all session loads/unloads in the
 * store to be sure that all sub parts are loaded.
 */
export default {
  async loadConfig() {
    // Be sure these are all lazy load as needed
    return Promise.all([
      store.dispatch('agents/fetchAgents'),
      store.dispatch('agents/fetchAgentStatuses'),
      store.dispatch('aiAgents/fetchAiAgents'),
      store.dispatch('aiContextConfigs/fetchAiContextConfigs'),
      store.dispatch('airbnb/fetchAirbnbAccounts'),
      store.dispatch('appConfig/fetchAppConfig'),
      store.dispatch('brands/fetchBrands'),
      store.dispatch('callResults/fetchCallResults'),
      store.dispatch('callStatus/fetchCallStatus'),
      store.dispatch('campaigns/fetchCampaigns'),
      store.dispatch('company/fetchCompany'),
      store.dispatch('conditions/fetchConditionConfig'),
      store.dispatch('connectors/fetchConnectors'),
      store.dispatch('contentBlocks/fetchBlocks'),
      store.dispatch('customFields/fetchCustomFields'),
      store.dispatch('dialDestinations/fetchDialDestinations'),
      store.dispatch('folders/fetchFolders'),
      store.dispatch('formFieldProfiles/fetchFormFieldProfiles'),
      store.dispatch('groups/fetchGroups'),
      store.dispatch('interests/fetchInterests'),
      store.dispatch('knowledgeTypes/fetchKnowledgeTypes'),
      store.dispatch('leadCategory/fetchLeadCategories'),
      store.dispatch('leadStatus/fetchLeadStatuses'),
      store.dispatch('leadSource/fetchLeadSources'),
      store.dispatch('leadSource/fetchLeadSourceDetails'),
      store.dispatch('leadType/fetchLeadTypes'),
      store.dispatch('lostReason/fetchLostReasons'),
      store.dispatch('lostReason/fetchLostReasonDetails'),
      store.dispatch('messages/fetchDrafts'),
      store.dispatch('phoneNumbers/fetchPhoneNumbers'),
      store.dispatch('queues/fetchQueues'),
      store.dispatch('reservations/fetchReservationTypes'),
      store.dispatch('surveys/fetchSurveys'),
      store.dispatch('tags/fetchTags'),
      store.dispatch('tasks/fetchTaskPriorities'),
      store.dispatch('taskTypes/fetchTaskTypes'),
      store.dispatch('team/fetchTeam'),
      store.dispatch('templates/fetchTemplates'),
      store.dispatch('postcardTemplates/fetchPostcardTemplates'),
      store.dispatch('units/fetchUnits'),
      store.dispatch('units/fetchUnitTypes'),
      store.dispatch('voice/fetchCallPlans'),
      store.dispatch('voice/fetchMenus'),
      store.dispatch('voice/fetchVoicemailBoxes'),
      store.dispatch('voice/fetchDirectDials'),
      store.dispatch('webhooks/fetchWebhooks'),
      store.dispatch('workflows/fetchWorkflows'),
      store.dispatch('workflows/fetchTriggerConfig'),
    ]);
  },
  // Unloads all config so that they can be lazy loaded with a new context
  unloadConfig() {
    store.dispatch('agents/unloadAgents');
    store.dispatch('agents/unloadAgentStatuses');
    store.dispatch('aiAgents/unloadAiAgents');
    store.dispatch('aiContextConfigs/unloadAiContextConfigs');
    store.dispatch('airbnb/unloadAirbnbAccounts');
    store.dispatch('appConfig/unloadAppConfig');
    store.dispatch('brands/unloadBrands');
    store.dispatch('callResults/unloadCallResults');
    store.dispatch('callStatus/unloadCallStatus');
    // Breaking with convention a bit. Only unloading as one call
    // The loading is all done lazily into the store as needed as the number of configs
    // is getting large. We cna see if this makes sense for other items too
    store.dispatch('callTypeDetails/unloadCallTypeDetails');
    store.dispatch('campaigns/unloadCampaigns');
    store.dispatch('company/unloadCompany');
    store.dispatch('conditions/unloadConditionConfig');
    store.dispatch('connectors/unloadConnectors');
    store.dispatch('contentBlocks/unloadBlocks');
    store.dispatch('customFields/unloadCustomFields');
    store.dispatch('dialDestinations/unloadDialDestinations');
    store.dispatch('folders/unloadFolders');
    store.dispatch('formFieldProfiles/unloadFormFieldProfiles'),
    store.dispatch('groups/unloadGroups');
    store.dispatch('interests/unloadInterests');
    store.dispatch('knowledgeTypes/unloadKnowledgeTypes');
    store.dispatch('leadCategory/unloadLeadCategories');
    store.dispatch('leadStatus/unloadLeadStatuses');
    store.dispatch('leadSource/unloadLeadSources');
    store.dispatch('leadSource/unloadLeadSourceDetails');
    store.dispatch('leadType/unloadLeadTypes');
    store.dispatch('lostReason/unloadLostReasons'),
    store.dispatch('lostReason/unloadLostReasonDetails'),
    store.dispatch('messages/unloadDrafts'),
    store.dispatch('phoneNumbers/unloadPhoneNumbers');
    store.dispatch('queues/unloadQueues');
    store.dispatch('reservations/unloadReservationTypes');
    store.dispatch('smsServices/unloadServices');
    store.dispatch('surveys/unloadSurveys');
    store.dispatch('tags/unloadTags');
    store.dispatch('tasks/unloadTaskPriorities'),
    store.dispatch('taskTypes/unloadTaskTypes'),
    store.dispatch('team/unloadTeam'),
    store.dispatch('templates/unloadTemplates'),
    store.dispatch('postcardTemplates/clearPostcardTemplates'),
    store.dispatch('units/unloadUnits'),
    store.dispatch('units/unloadUnitTypes'),
    store.dispatch('voice/unloadCallPlans');
    store.dispatch('voice/unloadMenus');
    store.dispatch('voice/unloadVoicemailBoxes');
    store.dispatch('voice/unloadDirectDials');
    store.dispatch('webhooks/unloadWebhooks');
    store.dispatch('workflows/unloadWorkflows');
    store.dispatch('workflows/unloadTriggerConfig');
  },

  /*
   * Load the flattened config for this user from the persisted config on the BE.
   */
  fetchUserConfig() {
    return authedAxios.get(AD_BASE_APPCONFIG_PATH).then(r => {
      return r;
    });
  },
};
